import React, { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";

const LottieLoader = dynamic(
  () =>
    import("@/components/Molecules/FullScreenLoader").then(
      (module) => module.LottieLoader
    ),
  { ssr: false }
);

import { SVGSprite } from "@/components/Atoms";

import { axios } from "../../../utils";
import { useWindowDimensions } from "@/hooks";

import {
  OverlayContent,
  StyledAction,
  StyledButton,
  StyledVideoOverlay,
  StyledYoutubePlayer,
} from "./style";

const VideoOverlay = ({ loading, thumbnail, onCLickHandler }) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 600;

  return (
    <StyledVideoOverlay
      className="videoOverlay"
      bgImg={thumbnail && `url(${thumbnail})`}
    >
      <OverlayContent>
        <StyledAction>
          <StyledButton variant="link" onClick={onCLickHandler}>
            {loading ? (
              <LottieLoader />
            ) : (
              <SVGSprite
                icon="icon-play-video"
                height={isMobile ? "60px" : "120px"}
                width={isMobile ? "60px" : "120px"}
              />
            )}
          </StyledButton>
        </StyledAction>
      </OverlayContent>
    </StyledVideoOverlay>
  );
};

function YoutubePlayer({ videoUrl, startTime, endTime, showControls }) {
  const { width } = useWindowDimensions();
  const isMobile = width <= 600;

  const [loading, setLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState("");
  const [showOverlay, setShowOverlay] = useState(true);
  const [videoId, setVideoId] = useState("");

  useEffect(() => {
    if (videoUrl) {
      const youtubeIDParser = (url) => {
        const regExp =
          /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[7].length === 11 ? match[7] : false;
      };

      const _videoId = youtubeIDParser(videoUrl);
      setVideoId(_videoId);

      // const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${_videoId}&format=json`;
      // axios.get(url).then((res) => setThumbnail(res?.thumbnail_url));
      setThumbnail(`https://i.ytimg.com/vi/${_videoId}/hqdefault.jpg`);
    }
  }, [videoUrl]);

  const playerRef = useRef();

  const [playerState, setPlayerState] = useState(0);

  let url = `https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&autoplay=1&modestbranding=1&rel=0&playsinline=${
    isMobile ? "1" : "0"
  }&`;

  if (startTime) url = `${url}&start=${startTime}`;
  if (endTime) url = `${url}&end=${endTime}`;
  if (!showControls) url = `${url}&controls=0`;

  const playerId = "youtube-player";
  const tagId = "youtube-video-tag";

  const insertYoutubeIframeApiScriptToDOM = () => {
    const tag = document.createElement("script");
    tag.id = tagId;
    tag.defer = true;
    tag.src = "https://www.youtube.com/iframe_api";

    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  };

  const onYouTubeIframeAPIReady = () => {
    const player = new window.YT.Player(playerId, {
      videoId: videoId,
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    });
    playerRef.current = player;
  };

  useEffect(() => {
    if (videoId) {
      insertYoutubeIframeApiScriptToDOM();
    }
  }, [videoId, showOverlay]);

  const loadPlayer = () => {
    const element = document.getElementById(tagId);
    if (!element) {
      insertYoutubeIframeApiScriptToDOM();
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
      onYouTubeIframeAPIReady();
    } else {
      onYouTubeIframeAPIReady();
    }
  };

  const onPlayerReady = () => {
    setLoading(false);
    setShowOverlay(false);
    if (playerRef?.current?.playVideo) playerRef.current.playVideo();
  };

  const onPlayerStateChange = () => {
    if (playerRef?.current?.getPlayerState) {
      setPlayerState(playerRef.current.getPlayerState());
    }
  };

  return (
    <StyledYoutubePlayer>
      {showOverlay && (
        <VideoOverlay
          loading={loading}
          thumbnail={thumbnail}
          onCLickHandler={() => {
            setLoading(true);
            loadPlayer();
          }}
        />
      )}
      <div id={playerId} />
    </StyledYoutubePlayer>
  );
}

export default YoutubePlayer;
